import React, { useEffect, useState, useMemo, useCallback } from "react";
import {
  Card,
  CardBody,
  Col,
  Container,
  CardHeader,
  Row,
  Modal,
  ModalHeader,
  Form,
  ModalBody,
  Label,
  Input,
  FormFeedback
} from "reactstrap";
import * as moment from "moment";
import { Link } from "react-router-dom";
import Flatpickr from "react-flatpickr";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import TableContainer from "../../../Components/Common/TableContainer";
import DeleteModal from "../../../Components/Common/DeleteModal";
import { isEmpty } from "lodash";
import * as Yup from "yup";
import { useFormik } from "formik";
import { useSelector, useDispatch } from "react-redux";
import {
  getProjects as onGetProject,
  addNewProject as onAddNewProject,
  updateProject as onUpdateProject,
  deleteProject as onDeleteProject,
} from "../../../slices/thunks";

import Loader from "../../../Components/Common/Loader";
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import ExportCSVModal from "../../../Components/Common/ExportCSVModal";
import { createSelector } from "reselect";
import { useProfile } from "../../../Components/Hooks/UserHooks";

const selectProjectState = (state) => state.Project;

  const selectProjectProperties = createSelector(
    selectProjectState,
    (project) => ({
      projects: project.projects,
      isProjectSuccess: project.isProjectSuccess,
      error: project.error,
    })
  );

const ProjectManagement = () => {

  const [modal, setModal] = useState(false);
  const [activeTab, setActiveTab] = useState("1");


  const dispatch = useDispatch();

  const { projects, isProjectSuccess, error } = useSelector(selectProjectProperties);


  const { userProfile } = useProfile();
  const [pageSize, setPageSize] = useState(5);
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth <= 480) {
        setPageSize(5); 
      } else {
        setPageSize(10); 
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const isAdmin = userProfile?.user?.groups?.includes("Admin");
  const [project, setProject] = useState([]);

  const [isEdit, setIsEdit] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [deleteModalMulti, setDeleteModalMulti] = useState(false);

  const onClickDelete = (project) => {
    setProject(project);
    setDeleteModal(true);
  };

  const handleDeleteProject = () => {
    if (project) {
      dispatch(onDeleteProject(project.id));
      setDeleteModal(false);
    }
  };

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      id:(project && project.id) || "",
      name: (project && project.name) || "",
      description: (project && project.description) || "",
      starting_date: (project && project.starting_date) || "",
      estimated_finish_date: (project && project.estimated_finish_date) || "",
      actual_finish_date: (project && project.actual_finish_date) || "",
      created_at: (project && project.created_at) || "",
      active: (project && project.active) || true,
    },
    validationSchema: Yup.object({
      name: Yup.string()
        .required("Please Enter Name")
        .min(3, "Name must be at least 3 characters long"),
      description: Yup.string().required("Please Enter Description"),
      active: Yup.boolean().required("Please specify if the project is active"),
    }),

    onSubmit: (values) => {
      const sanitizeValues = (values) => {
        const sanitized = { ...values };
        for (let key in sanitized) {
          if (sanitized[key] === "") {
            sanitized[key] = null; 
          }
        }
        if (sanitized.active === "" || sanitized.active === null) {
          sanitized.active = true;
        }
        return sanitized;
      };

      const sanitizedValues = sanitizeValues(values);

      if (isEdit) {
        const updateProject = {
          id: project ? project.id : 0,
          name: sanitizedValues.name,
          description: sanitizedValues.description,
          starting_date: sanitizedValues.starting_date,
          estimated_finish_date: sanitizedValues.estimated_finish_date,
          actual_finish_date: sanitizedValues.actual_finish_date,
          created_at: sanitizedValues.created_at,
          active: sanitizedValues.active,
        };
        dispatch(onUpdateProject(updateProject));
        validation.resetForm();
      } else {
        const newProject = {
          name: sanitizedValues.name,
          description: sanitizedValues.description,
          starting_date: sanitizedValues.starting_date,
          estimated_finish_date: sanitizedValues.estimated_finish_date,
          actual_finish_date: sanitizedValues.actual_finish_date,
          active: sanitizedValues.active,
        };
        dispatch(onAddNewProject(newProject))
        validation.resetForm();
      }
      toggle();
    },
  });


  useEffect(() => {
      dispatch(onGetProject()).then((result) => {
      }).catch((error) => {
        console.error("Error fetching project:", error);
      });
  }, [dispatch]);

  useEffect(() => {
    setProject(projects);
  }, [projects]);

  useEffect(() => {
    if (!isEmpty(projects)) {
      setProject(projects);
      setIsEdit(false);
    }
  }, [projects]);

  const toggle = useCallback(() => {
    if (modal) {
      setModal(false);
      setProject(null);
    } else {
      setModal(true);
    }
  }, [modal]);

  const handleProjectClick = useCallback(
    (arg) => {
      const project = arg;
      setProject({
        id: project.id,
        name: project.name,
        description: project.description,
        starting_date: project.starting_date,
        estimated_finish_date: project.estimated_finish_date,
        actual_finish_date: project.actual_finish_date,
        created_at: project.created_at,
        active: project.active,
      });

      setIsEdit(true);
      toggle();
    },
    [toggle]
  );

  const checkedAll = useCallback(() => {
    const checkall = document.getElementById("checkBoxAll");
    const ele = document.querySelectorAll(".orderCheckBox");
    if (checkall.checked) {
      ele.forEach((ele) => {
        ele.checked = true;
      });
    } else {
      ele.forEach((ele) => {
        ele.checked = false;
      });
    }
    deleteCheckbox();
  }, []);

  const [selectedCheckBoxDelete, setSelectedCheckBoxDelete] = useState([]);
  const [isMultiDeleteButton, setIsMultiDeleteButton] = useState(false);

  const deleteMultiple = () => {
    const checkall = document.getElementById("checkBoxAll");
    selectedCheckBoxDelete.forEach((element) => {
      dispatch(onDeleteProjects(element.value));
      setTimeout(() => { toast.clearWaitingQueue(); }, 3000);
    });
    setIsMultiDeleteButton(false);
    checkall.checked = false;
  };

  const deleteCheckbox = () => {
    const ele = document.querySelectorAll(".orderCheckBox:checked");
    ele.length > 0 ? setIsMultiDeleteButton(true) : setIsMultiDeleteButton(false);
    setSelectedCheckBoxDelete(ele);
  };

  const columns = useMemo(
    () => [
      {
        header: "name",
        accessorKey: "name",
        enableColumnFilter: false,
        cell: (cell) => cell.getValue() || "N/A",
      },
      {
        header: "Description",
        accessorKey: "description",
        enableColumnFilter: false,
        cell: (cell) => cell.getValue() || "N/A",
      },
      {
        header: "Starting Date",
        accessorKey: "starting_date",
        enableColumnFilter: false,
        cell: (cell) => {
          const value = cell.getValue();
          if (value === null || value === undefined) {
            return <span style={{ color: "red" }}>No data</span>;
          }
          return (
            <>
              {handleValidDate(value)}
            </>
          );
        },
      },
      {
        header: "Estimated Finish Date",
        accessorKey: "estimated_finish_date",
        enableColumnFilter: false,
        cell: (cell) => {
          const value = cell.getValue();
          if (value === null || value === undefined) {
            return <span style={{ color: "red" }}>No data</span>;
          }
          return (
            <>
              {handleValidDate(value)}
            </>
          );
        },
      },
      {
        header: "Actual Finish Date",
        accessorKey: "actual_finish_date",
        enableColumnFilter: false,
        cell: (cell) => {
          const value = cell.getValue();
          if (value === null || value === undefined) {
            return <span style={{ color: "red" }}>No data</span>;
          }
          return (
            <>
              {handleValidDate(value)},
              <small className="text-muted"> {handleValidTime(value)}</small>
            </>
          );
        },
      },
      {
        header: "Active",
        accessorKey: "active",
        enableColumnFilter: false,
        cell: (cell) => (
          <>{cell.getValue() ? "True" : "False"}</>
        ),
      },

      {
        header: "Action",
        cell: (cellProps) => {
          const isAdmin = userProfile?.user?.groups?.includes("Admin");

          return (
            <ul className="list-inline hstack gap-2 mb-0">
              <li className="list-inline-item">
              <Link
                to={`/project-details/${cellProps.row.original.id}`}
                className="text-primary d-inline-block"
              >
                <i className="ri-eye-fill fs-16"></i>
              </Link>
              </li>
              {isAdmin && (
                <>
                  <li className="list-inline-item edit">
                    <Link
                      to="#"
                      className="text-primary d-inline-block edit-item-btn"
                      onClick={() => {
                        const projectData = cellProps.row.original;
                        handleProjectClick(projectData);
                      }}
                    >
                      <i className="ri-pencil-fill fs-16"></i>
                    </Link>
                  </li>
                  <li className="list-inline-item">
                    <Link
                      to="#"
                      className="text-danger d-inline-block remove-item-btn"
                      onClick={() => {
                        const projectData = cellProps.row.original;
                        onClickDelete(projectData);
                      }}
                    >
                      <i className="ri-delete-bin-5-fill fs-16"></i>
                    </Link>
                  </li>
                </>
              )}
            </ul>
          );
        },
      },
    ],
    [handleProjectClick, checkedAll]
  );


  const defaultdate = () => {
    let d = new Date(),
      months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    let h = (d.getHours() % 12) || 12;
    let ampm = d.getHours() < 12 ? "AM" : "PM";
    return ((d.getDate() + ' ' + months[d.getMonth()] + ', ' + d.getFullYear() + ", " + h + ":" + d.getMinutes() + " " + ampm).toString());
  };


  const [date, setDate] = useState(defaultdate());

  const handleValidDate = (date) => {
    return moment(date).format("DD MMM YYYY");
  };

  const handleValidTime = (time) => {
    const timeObj = new Date(time);
    const formattedTime = moment(timeObj).format("hh:mm A");
    return formattedTime;
  };

  const [isExportCSV, setIsExportCSV] = useState(false);

  return (
    <div className="page-content">
      <ExportCSVModal
        show={isExportCSV}
        onCloseClick={() => setIsExportCSV(false)}
        data={projects}
      />

      <DeleteModal
        show={deleteModal}
        onDeleteClick={handleDeleteProject}
        onCloseClick={() => setDeleteModal(false)}
      />
      <DeleteModal
        show={deleteModalMulti}
        onDeleteClick={() => {
          deleteMultiple();
          setDeleteModalMulti(false);
        }}
        onCloseClick={() => setDeleteModalMulti(false)}
      />
      <Container fluid>
        {/* <BreadCrumb title="Project" pageTitle="Tables" /> */}
        <Row>
          <Col lg={12}>
            <Card id="orderList">
              <CardHeader className="border-0">
                <Row className="align-items-center gy-3">
                  <div className="col-sm">
                  </div>
                  <div className="col-sm-auto" style={{marginBottom: "10px"}}>
                    <div className="d-flex gap-1 flex-wrap">
                      {isAdmin && <button
                        type="button"
                        className="btn btn-success add-btn"
                        id="create-btn"
                        onClick={() => { setIsEdit(false); toggle(); }}
                      >
                        <i className="ri-add-line align-bottom me-1"></i> Create
                        Project
                      </button>}
                      <button type="button" className="btn btn-info" onClick={() => setIsExportCSV(true)}>
                        <i className="ri-file-download-line align-bottom me-1"></i>{" "}
                        Export
                      </button>
                      {" "}
                      {isMultiDeleteButton && <button className="btn btn-soft-danger"
                        onClick={() => setDeleteModalMulti(true)}
                      ><i
                        className="ri-delete-bin-2-line"></i></button>}
                    </div>
                  </div>
                </Row>
              </CardHeader>

              <CardBody className="pt-0">
                <div>
                  {isProjectSuccess ? (
                    projects.length > 0 ? (
                      <TableContainer
                        columns={columns}
                        data={projects || []}
                        isGlobalFilter={true}
                        customPageSize={pageSize}
                        divClass="table-responsive table-card mb-1"
                        tableClass="align-middle table-nowrap"
                        theadClass="table-light text-muted text-uppercase"
                        handleProjectClick={handleProjectClick}
                        // isProjectFilter={true}
                        SearchPlaceholder="Search for ID, name, active or something..."
                      />
                    ) : (
                      <div className="text-center mt-5">
                        <p className="text-danger">No projects are available. Please add a project.</p>
                      </div>
                    )
                  ) : (
                    <Loader error={error} />
                  )}
                </div>
                <Modal id="showModal" isOpen={modal} toggle={toggle} centered>
                  <ModalHeader className="bg-light p-3" toggle={toggle}>
                    {!!isEdit ? "Edit Project" : "Add Project"}
                  </ModalHeader>

                  <Form className="tablelist-form" onSubmit={(e) => {
                    e.preventDefault();
                    validation.handleSubmit();
                    return false;
                  }}>
                    <ModalBody>
                      <input type="hidden" id="id-field" />

                      <div className="mb-3">
                        <Label
                          htmlFor="id-field"
                          className="form-label"
                        >
                          Name
                        </Label>
                        <Input
                          name="name"
                          id="id-field"
                          className="form-control"
                          placeholder="Enter Name"
                          type="text"
                          validate={{
                            required: { value: true },
                          }}
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                          value={validation.values.name || ""}
                          invalid={
                            validation.touched.name && validation.errors.name ? true : false
                          }
                        />
                        {validation.touched.name && validation.errors.name ? (
                          <FormFeedback type="invalid">{validation.errors.name}</FormFeedback>
                        ) : null}

                      </div>

                      <div className="mb-3">
                      <Label htmlFor="exampleFormControlTextarea5" className="form-label">Description</Label>
                      <Input
                        name="description"
                        id="exampleFormControlTextarea5"
                        className="form-control"
                        type="textarea"
                        rows="3"
                        placeholder="Enter Description"
                        onChange={validation.handleChange}
                        onBlur={validation.handleBlur}
                        value={validation.values.description || ""}
                        invalid={
                          validation.touched.description && validation.errors.description ? true : false
                        }
                      />
                      {validation.touched.description && validation.errors.description ? (
                          <FormFeedback type="invalid">{validation.errors.description}</FormFeedback>
                        ) : null}

                    </div>

                      <div className="mb-3">
                        <Label htmlFor="date-field" className="form-label">
                        Starting Date
                        </Label>

                        <Flatpickr
                          name="starting_date"
                          className="form-control"
                          id="datepicker-publish-input"
                          placeholder="Select a date"
                          options={{
                            enableTime: true,
                            altInput: true,
                            altFormat: "d M, Y, G:i K",
                            dateFormat: "d M, Y, G:i K",
                          }}
                          onChange={(date) =>
                            validation.setFieldValue("starting_date", moment(date[0]).toISOString())
                          }
                          onBlur={validation.handleBlur}
                          value={validation.values.starting_date || ""}
                        />
                      </div>
                      <div className="mb-3">
                        <Label htmlFor="date-field" className="form-label">
                        Estimated Finish Date
                        </Label>

                        <Flatpickr
                          name="estimated_finish_date"
                          className="form-control"
                          id="datepicker-publish-input"
                          placeholder="Select a date"
                          options={{
                            enableTime: true,
                            altInput: true,
                            altFormat: "d M, Y, G:i K",
                            dateFormat: "d M, Y, G:i K",
                          }}
                          onChange={(date) =>
                            validation.setFieldValue("estimated_finish_date", moment(date[0]).toISOString())
                          }
                          value={validation.values.estimated_finish_date || ""}
                        />
                      </div>
                      <div className="mb-3">
                        <Label htmlFor="date-field" className="form-label">
                        Actual Finish Date
                        </Label>

                        <Flatpickr
                          name="actual_finish_date"
                          className="form-control"
                          id="datepicker-publish-input"
                          placeholder="Select a date"
                          options={{
                            enableTime: true,
                            altInput: true,
                            altFormat: "d M, Y, G:i K",
                            dateFormat: "d M, Y, G:i K",
                          }}
                          onChange={(date) =>
                            validation.setFieldValue("actual_finish_date", moment(date[0]).toISOString())
                          }
                          value={validation.values.actual_finish_date || ""}
                        />
                      </div>

                      <div>
                        <Label htmlFor="delivered-status" className="form-label">
                          Active
                        </Label>

                        <Input
                          name="active"
                          type="select"
                          className="form-select"
                          onChange={(e) => validation.setFieldValue('active', e.target.value === 'true')}
                          onBlur={validation.handleBlur}
                          value={validation.values.active !== undefined ? (validation.values.active ? "true" : "false") : "true"}
                        >
                          <option value="true">True</option>
                          <option value="false">False</option>
                        </Input>

                        {validation.touched.active && validation.errors.active ? (
                          <FormFeedback type="invalid">
                            {validation.errors.active}
                          </FormFeedback>
                        ) : null}
                      </div>


                    </ModalBody>
                    <div className="modal-footer">
                      <div className="hstack gap-2 justify-content-end">
                        <button
                          type="button"
                          className="btn btn-light"
                          onClick={() => {
                            setModal(false);
                          }}
                        >
                          Close
                        </button>

                        <button type="submit" className="btn btn-success">
                          {isEdit
                            ? "Update"
                            : "Add Project"}
                        </button>
                      </div>
                    </div>
                  </Form>
                </Modal>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default ProjectManagement;


